'use client';

import DOMPurify from 'isomorphic-dompurify';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeStringify from 'rehype-stringify';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import type { DetailsOpenState } from './details-open-state';
import type { GithubMarkdownRenderBatch } from './github-markdown-render-batch';
import { applyDetailsOpenState, captureDetailsOpenState } from './details-open-state';
import { containsGithubUploadUrl, fetchGithubMarkdown } from './github-markdown-render-batch';
import { formatDetailsHtml } from './html-processing';
import { installPictureSourceFallback } from './picture-fallback';
import rehypePullRequestReferences from './rehype-pull-request-references';
import remarkCommitReferences from './remark-commit-references';
import remarkPullRequestReferences from './remark-pull-request-references';
import remarkVideoAsImage from './remark-video-as-image';
import { ImageDialog } from '@/components/image-dialog';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/utils/clipboard';
import { highlightCodeBlocks } from '@/utils/code-block-syntax-highlighter';
import { getGithubRepoImageProxyUrl } from '@/utils/github/repo-image-url';
import { cleanupRenderedHtml, processHtml } from '@/utils/html-processor';
import { CHECK_ICON_SVG, CLIPBOARD_ICON_SVG } from '@/utils/icon-svgs';
import { purifyConfig } from '@/utils/purify-config';

// Define allowed tags directly in the component to ensure they're always up-to-date
const ALLOWED_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br', 'p', 'a', 'img', 'picture', 'ul', 'ol', 'li', 'code', 'pre', 'blockquote', 'strong', 'em', 'video', 'source', 'details', 'summary', 'svg', 'path', 'span', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'b', 'i', 'hr', 'kbd', 'mark', 'samp', 'sub', 'sup'];

/** What makes the renderer replace its own HTML with GitHub's render of the content. */
export type MarkdownGithubRender = 'github-content' | 'uploads';

/**
 * Props for the MarkdownRenderer component
 */
type MarkdownRendererProps = {
  /** The markdown content to render */
  content: string;
  /** The GitHub organization name - used for resolving private GitHub image URLs */
  org: string;
  /** The GitHub repository name - used for resolving private GitHub image URLs */
  repo: string;
  /** Optional ID for the markdown container element */
  id?: string;
  /** Optional className to override default styles */
  className?: string;
  /** Optional base text size to override default styles */
  baseTextSize?: 'sm';
  /** Compact tightens vertical rhythm (margins/line-height) without changing font sizes */
  density?: 'compact';
  /** Whether this is rendering for a public view (disables authenticated API calls) */
  isPublicView?: boolean;
  /**
   * Content that switches to GitHub's render: any GitHub link or reference (`github-content`,
   * the default), or only an uploaded image or video (`uploads`). Public views never switch.
   */
  githubRender?: MarkdownGithubRender;
  /** Internal shared upgrade request owned by the mixed markdown renderer. */
  githubRenderBatch?: GithubMarkdownRenderBatch;
  /** Whether raw HTML should be displayed literally instead of being interpreted */
  renderHtmlAsText?: boolean;
  /** Whether same- and cross-repository pull request references should render as links */
  linkifyPullRequestReferences?: boolean;
  /** Whether bare commit SHAs should link to the commit on GitHub */
  linkifyCommitReferences?: boolean;
};
type ProcessMarkdownOptions = {
  content: string;
  linkifyCommitReferences: boolean;
  linkifyPullRequestReferences: boolean;
  org: string;
  proxyRepoImages: boolean;
  renderHtmlAsText: boolean;
  repo: string;
};
type ProcessedMarkdown = {
  hasLinkifiablePullRequestReference: boolean;
  html: string;
};
function escapeRawHtml(content: string): string {
  return content.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Synchronously converts markdown to sanitized HTML so the component can
 * render content immediately on mount without an async flash of empty state.
 *
 * `proxyRepoImages` loads images committed to a repository through cubic, because their GitHub
 * file URLs load only in a browser signed in to github.com.
 */
function sanitizeMarkdownHtml(html: string, {
  proxyRepoImages
}: {
  proxyRepoImages: boolean;
}): string {
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    const src = node.tagName === 'IMG' && proxyRepoImages ? node.getAttribute('src') : null;
    const proxyUrl = src ? getGithubRepoImageProxyUrl(src) : null;
    if (proxyUrl) {
      node.setAttribute('src', proxyUrl);
    }
  });
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR: purifyConfig.ALLOWED_ATTR,
      ADD_ATTR: purifyConfig.ADD_ATTR,
      FORBID_TAGS: purifyConfig.FORBID_TAGS,
      FORBID_ATTR: purifyConfig.FORBID_ATTR
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}
function processMarkdownSync({
  content,
  linkifyCommitReferences,
  linkifyPullRequestReferences,
  org,
  proxyRepoImages,
  renderHtmlAsText,
  repo
}: ProcessMarkdownOptions): ProcessedMarkdown {
  if (!content) {
    return {
      hasLinkifiablePullRequestReference: false,
      html: ''
    };
  }
  try {
    const normalizedContent = renderHtmlAsText ? escapeRawHtml(content) : content;
    let hasLinkifiablePullRequestReference = false;
    const processor = unified().use(remarkParse).use(remarkVideoAsImage).use(remarkBreaks).use(remarkGfm);
    if (linkifyPullRequestReferences) {
      processor.use(remarkPullRequestReferences, {
        org,
        repo,
        onReference: () => {
          hasLinkifiablePullRequestReference = true;
        }
      });
    }
    if (linkifyCommitReferences) {
      processor.use(remarkCommitReferences, {
        org,
        repo
      });
    }
    const result = processor.use(remarkRehype, {
      allowDangerousHtml: true
    }).use(rehypeRaw).use(rehypeStringify).processSync(normalizedContent);
    const processed = processHtml(String(result));

    // Sanitize last: cleanup rewrites the HTML as a string on the server, so nothing
    // may modify the markup after DOMPurify has run.
    return {
      hasLinkifiablePullRequestReference,
      html: sanitizeMarkdownHtml(cleanupRenderedHtml(processed), {
        proxyRepoImages
      })
    };
  } catch {
    return {
      hasLinkifiablePullRequestReference: false,
      html: ''
    };
  }
}
function preserveUnresolvedPullRequestReferences(html: string, options: Pick<ProcessMarkdownOptions, 'org' | 'repo'>): string {
  const processor = unified().use(rehypeRaw).use(rehypePullRequestReferences, options).use(rehypeStringify);
  const tree = processor.runSync({
    type: 'root',
    children: [{
      type: 'raw',
      value: html
    }]
  });
  return processor.stringify(tree);
}
function createClipboardResetTimers() {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  return {
    schedule(button: HTMLButtonElement) {
      const timer = setTimeout(() => {
        button.innerHTML = CLIPBOARD_ICON_SVG;
        timers.delete(timer);
      }, 2000);
      timers.add(timer);
    },
    cancel() {
      for (const timer of timers) {
        clearTimeout(timer);
      }
      timers.clear();
    }
  };
}

/**
 * Renders markdown content with GitHub-compatible formatting and features.
 *
 * Features:
 * - GitHub Flavored Markdown (GFM) support via remark-gfm
 * - Two-phase rendering for optimal performance:
 *   1. Quick client-side render for immediate feedback
 *   2. GitHub API render for perfect compatibility (only if GitHub-specific content is detected)
 * - Private GitHub image support
 * - XSS protection via DOMPurify
 * - Image lightbox/modal support
 * - Responsive tables and images
 * - Custom styling via Tailwind prose classes
 * - Code blocks with syntax highlighting and copy button
 *
 * @example
 * ```tsx
 * <MarkdownRenderer
 *   content="# Hello\nThis is **markdown** content"
 *   org="my-org"
 *   repo="my-repo"
 *   className="custom-styles"
 * />
 * ```
 */
const MarkdownRendererComponent = ({
  content,
  org,
  repo,
  id,
  className,
  baseTextSize,
  density,
  isPublicView = false,
  githubRender = 'github-content',
  githubRenderBatch,
  renderHtmlAsText = false,
  linkifyPullRequestReferences = false,
  linkifyCommitReferences = false
}: MarkdownRendererProps) => {
  // Synchronous initial HTML prevents flash of empty content on mount/remount.
  const syncRender = useMemo(() => processMarkdownSync({
    content,
    linkifyCommitReferences,
    linkifyPullRequestReferences,
    org,
    // Public views make no authenticated calls, and only show public repositories, whose
    // GitHub image URLs load as they are.
    proxyRepoImages: !isPublicView,
    renderHtmlAsText,
    repo
  }), [content, isPublicView, linkifyCommitReferences, linkifyPullRequestReferences, org, renderHtmlAsText, repo]);
  const syncHtml = syncRender.html;
  const renderKey = `${org}:${repo}:${isPublicView}:${githubRender}:${renderHtmlAsText}:${linkifyPullRequestReferences}:${linkifyCommitReferences}:${content}`;
  const [asyncHtml, setAsyncHtml] = useState<{
    key: string;
    html: string;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const detailsOpenStateRef = useRef<{
    key: string;
    states: DetailsOpenState[];
  } | null>(null);

  // Only use the async upgrade when it was generated for the current content.
  const html = asyncHtml?.key === renderKey ? asyncHtml.html : syncHtml;
  // React compares dangerouslySetInnerHTML by object identity before assigning
  // innerHTML. Keep this object stable while the HTML is unchanged so unrelated
  // renderer updates cannot replace native stateful nodes such as <details>.
  const dangerouslySetInnerHtml = useMemo(() => ({
    __html: html
  }), [html]);
  useEffect(() => {
    const needsGithubRender = githubRender === 'uploads' ? containsGithubUploadUrl(content) : content.includes('https://github.com/') || linkifyPullRequestReferences && syncRender.hasLinkifiablePullRequestReference;

    // Skip GitHub API upgrade for public views or content without GitHub-specific markup.
    if (!content || renderHtmlAsText || isPublicView || !needsGithubRender) {
      return;
    }
    let isMounted = true;
    const controller = new AbortController();
    const upgradeWithGithubRender = async () => {
      try {
        const githubHtml = githubRenderBatch ? await githubRenderBatch.render(content) : await fetchGithubMarkdown(content, org, repo, controller.signal);
        if (!isMounted) {
          return;
        }

        // GitHub's render links commit SHAs itself, and only those of commits that exist.
        const githubHtmlWithReferences = linkifyPullRequestReferences ? preserveUnresolvedPullRequestReferences(githubHtml, {
          org,
          repo
        }) : githubHtml;
        let processedGithubHtml = processHtml(githubHtmlWithReferences);
        // For final GitHub HTML – just ensure closed state
        processedGithubHtml = formatDetailsHtml(processedGithubHtml);
        // Public views never reach this upgrade, so repository images always load through cubic.
        const cleanedGithubHtml = sanitizeMarkdownHtml(cleanupRenderedHtml(processedGithubHtml), {
          proxyRepoImages: true
        });
        setAsyncHtml({
          key: renderKey,
          html: cleanedGithubHtml
        });
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error processing markdown:', error);
        }
      }
    };
    upgradeWithGithubRender();
    return () => {
      isMounted = false;
      if (!githubRenderBatch) {
        controller.abort();
      }
    };
  }, [content, org, repo, renderKey, isPublicView, githubRender, githubRenderBatch, linkifyPullRequestReferences, renderHtmlAsText, syncRender.hasLinkifiablePullRequestReference]);

  // The GitHub-rendered upgrade replaces the entire innerHTML subtree. Preserve
  // native <details> state for the same content so a disclosure opened while
  // the request was in flight does not snap closed when the response arrives.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Enhance code blocks in place after hydration or a content update.
    // Both HTML sources stay DOM-free and share this browser-only step.
    highlightCodeBlocks(container);
    const previousState = detailsOpenStateRef.current;
    if (previousState?.key === renderKey) {
      applyDetailsOpenState(container, previousState.states);
    }
    const captureCurrentState = () => {
      detailsOpenStateRef.current = {
        key: renderKey,
        states: captureDetailsOpenState(container)
      };
    };
    captureCurrentState();

    // toggle does not bubble, so listen in the capture phase for disclosures
    // inside the raw HTML subtree.
    container.addEventListener('toggle', captureCurrentState, true);
    return () => container.removeEventListener('toggle', captureCurrentState, true);
  }, [html, renderKey]);
  useLayoutEffect(() => {
    const container_0 = containerRef.current;
    if (!container_0) {
      return;
    }
    return installPictureSourceFallback(container_0);
  }, [html]);

  // Add clipboard buttons to code blocks after the HTML is updated
  useEffect(() => {
    const container_1 = containerRef.current;
    if (!container_1) {
      return;
    }
    let cancelled = false;
    const resetTimers = createClipboardResetTimers();
    const codeByButton = new WeakMap<HTMLButtonElement, string>();
    const handleCopy = (event: MouseEvent) => {
      const target = event.target;
      const button = target instanceof Element ? target.closest<HTMLButtonElement>('.clipboard-btn') : null;
      const codeContent = button ? codeByButton.get(button) : undefined;
      if (!button || codeContent === undefined) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      // copyToClipboard reports its own failures and resolves false.
      void copyToClipboard(codeContent, {
        errorMessage: 'Failed to copy code'
      }).then(success => {
        if (cancelled) {
          return;
        }
        if (success) {
          button.innerHTML = CHECK_ICON_SVG;
          resetTimers.schedule(button);
        }
      });
    };
    container_1.addEventListener('click', handleCopy);

    // Create a function to add clipboard buttons to all code blocks in the container
    const addClipboardButtons = () => {
      // Find all code blocks
      const preElements = container_1.querySelectorAll('pre');
      preElements.forEach((preElement, index) => {
        const id_0 = `code-block-${index}`;
        preElement.id = id_0;

        // Get the code content
        const codeContent_0 = preElement.getAttribute('data-code-content') || preElement.textContent || '';

        // Check if this pre element already has a clipboard button
        const existingButton = preElement.querySelector<HTMLButtonElement>('.clipboard-btn');
        if (existingButton) {
          codeByButton.set(existingButton, codeContent_0);
          return;
        }
        preElement.classList.add('relative');

        // Create a wrapper element to hold the clipboard button
        const wrapperElement = document.createElement('div');
        wrapperElement.className = 'clipboard-btn-wrapper';
        wrapperElement.style.position = 'absolute';
        wrapperElement.style.top = '8px';
        wrapperElement.style.right = '8px';

        // Create the clipboard button element
        const buttonElement = document.createElement('button');
        buttonElement.className = 'clipboard-btn p-1 rounded text-zinc-400 opacity-0 group-hover:opacity-80 hover:bg-zinc-700 hover:opacity-100 cursor-pointer';
        buttonElement.setAttribute('aria-label', 'Copy code to clipboard');
        buttonElement.innerHTML = CLIPBOARD_ICON_SVG;
        codeByButton.set(buttonElement, codeContent_0);

        // Add the button to the wrapper and the wrapper to the pre element
        wrapperElement.appendChild(buttonElement);
        preElement.appendChild(wrapperElement);
      });
    };

    // Add clipboard buttons immediately after render
    const timeoutId = setTimeout(addClipboardButtons, 0);

    // Setup a MutationObserver to watch for changes in the container and add clipboard buttons when needed
    const observer = new MutationObserver(addClipboardButtons);
    observer.observe(container_1, {
      childList: true,
      subtree: true
    });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      container_1.removeEventListener('click', handleCopy);
      resetTimers.cancel();
      observer.disconnect();
    };
  }, [html]);

  // Early return if no content
  if (!content) {
    return null;
  }
  return <>
      <div ref={containerRef} id={id ? `markdown-container-${id}` : 'markdown-container'}
    // Marks rendered markdown for MARKDOWN_RENDERER_LINK_SELECTOR.
    data-markdown-renderer="" className={cn(`prose prose-zinc-dark
          prose-pre:code:bg-transparent
          prose-pre:code:p-0
          prose-invert max-w-full
          overflow-auto text-wrap 
          prose-h1:text-2xl prose-h1:font-semibold prose-h1:mb-1! prose-h1:mt-4!
          prose-h2:text-lg prose-h2:font-semibold prose-h2:mb-1! prose-h2:mt-4!
          prose-h3:font-semibold prose-h3:text-[16px] prose-h3:mb-1! prose-h3:mt-4!
          prose-h4:mt-1! prose-h4:font-semibold prose-h4:leading-relaxed
          prose-p:text-base prose-p:font-normal prose-p:leading-relaxed
          prose-a:text-inherit prose-a:hover:text-inherit prose-code:rounded
          prose-code:bg-zinc-800 prose-code:px-1 prose-code:py-0.5
          prose-code:text-zinc-100 prose-code:before:content-none prose-code:after:content-none
          prose-pre:rounded-lg prose-pre:bg-zinc-800 prose-pre:p-4 prose-ul:my-2
          prose-ul:list-disc prose-ul:pl-6 prose-ul:text-base
          prose-li:my-2
          prose-img:my-8 prose-img:cursor-pointer
          prose-img:rounded-lg
          [&_video]:my-8 [&_video]:w-full [&_video]:max-w-3xl [&_video]:rounded-lg
          [&_details_video]:my-0
          prose-table:w-full
          prose-table:my-4
          prose-hr:my-4
          [&_p:has(strong.font-semibold)]:mb-2
          [&_p:has(>sup)]:mt-2.5
          [&_p:has(>sup)]:text-zinc-500
          [&_p:has(>sub)]:mt-2.5
          [&_p:has(>sub)]:text-zinc-500
          [&_sup]:text-[11px]
          [&_sup]:leading-[1.4]
          [&_sup]:text-inherit
          [&_sup_code]:text-inherit
          [&_sup_code]:leading-inherit
          [&_sub]:text-[11px]
          [&_sub]:leading-[1.4]
          [&_sub]:text-inherit
          [&_sub_code]:text-inherit
          [&_sub_code]:leading-inherit
          prose-table:border-collapse
          prose-thead:bg-zinc-800
          prose-th:p-2
          prose-th:text-left
          prose-th:font-semibold
          prose-td:p-2
          prose-td:border-t
          prose-td:border-zinc-800
          [&_pre_code_.line]:border-l-0
          [&_pre_code_.line]:border-r-0
          [&_pre_code_.line]:pl-0
          [&_pre_code_.line-number]:text-gray-500
          [&_pre_.has-line-numbers]:pl-0
          [&_summary]:cursor-pointer
          [&_summary]:text-zinc-300
          [&_summary:hover]:text-zinc-50
          markdown-syntax-highlighted`, baseTextSize === 'sm' && cn('prose-h1:text-[22px] prose-h2:text-[17px] prose-h3:text-[15px]', 'prose-p:text-[14px] prose-ul:text-[14px] prose-ol:text-[14px] prose-li:text-[14px]', '[&_blockquote]:text-[14px] [&_td]:text-[14px] [&_th]:text-[14px] [&_summary]:text-[14px]', '[&_code]:text-[13px] [&_pre]:text-[13px]'),
    // Overrides the looser defaults above via twMerge; font sizes stay untouched.
    density === 'compact' && cn('prose-p:my-1.5 prose-p:leading-normal prose-li:leading-normal', 'prose-h1:mt-2! prose-h2:mt-2! prose-h3:mt-2!', 'prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5', 'prose-img:my-3 [&_video]:my-3', 'prose-pre:p-3 prose-table:my-2'), className)} dangerouslySetInnerHTML={dangerouslySetInnerHtml} />

      <ImageDialog containerId={id ? `markdown-container-${id}` : 'markdown-container'} />
    </>;
};
export const MarkdownRenderer = memo(MarkdownRendererComponent);
MarkdownRenderer.displayName = 'MarkdownRenderer';